import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import type { ActionGroup } from '../../lib/dashboard-health';
import { approveBooking } from '../../lib/facilities-api';
import { extractApiError } from '../../lib/api-client';
import { formatRupiah } from '../../lib/format';
import { useLanguage } from '../../i18n';

const DOT = { EMERGENCY: 'error.main', BOOKING: 'warning.main', COMPLAINT: 'warning.main', ARREARS: 'error.main' } as const;

/**
 * "Perlu tindakan": one line per kind of task with its most useful action.
 * Bookings can be approved in place; everything else links to its page.
 */
export function GroupedActions({
  groups,
  onBookingApproved,
}: {
  groups: ActionGroup[];
  onBookingApproved: (id: string) => void;
}) {
  const { lang, t } = useLanguage();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const when = (iso: string) =>
    new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'id-ID', {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });

  async function approve(id: string) {
    setBusy(id);
    setError(null);
    try {
      await approveBooking(id);
      onBookingApproved(id);
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusy(null);
    }
  }

  const row = (key: string, kind: ActionGroup['kind'], title: string, detail: string, actions: ReactNode) => (
    <Stack key={key} direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DOT[kind], flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>{detail}</Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>{actions}</Stack>
    </Stack>
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>{t('dash.action.title')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('dash.action.subtitle')}</Typography>
        {error ? <Typography variant="caption" color="error" component="div" sx={{ mt: 1 }}>{error}</Typography> : null}
      </CardContent>
      {groups.length === 0 ? (
        <CardContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary">{t('dash.action.empty')}</Typography>
        </CardContent>
      ) : (
        <Box sx={{ pb: 1 }}>
          {groups.flatMap((g) => {
            switch (g.kind) {
              case 'EMERGENCY':
                return [row('emergency', g.kind, t('dash.group.emergency', { count: g.count }), t('dash.group.emergencyDetail'), (
                  <Button size="small" variant="contained" color="error" component={RouterLink} to="/operations">{t('dash.group.open')}</Button>
                ))];
              case 'BOOKING':
                // Each booking gets its own line so it can be approved right here.
                return g.items.slice(0, 3).map((b) => row(`booking-${b.id}`, g.kind,
                  t('dash.group.booking', { facility: b.facility?.name ?? '—' }),
                  `${t('dash.group.bookingWaiting')} · ${when(b.start_at)}${b.notes ? ` · ${b.notes}` : ''}`,
                  <>
                    <Button size="small" variant="contained" onClick={() => void approve(b.id)} disabled={busy === b.id} sx={{ minWidth: 84 }}>
                      {busy === b.id ? <CircularProgress size={16} color="inherit" /> : t('dash.group.approve')}
                    </Button>
                    <Button size="small" component={RouterLink} to="/bookings">{t('dash.group.details')}</Button>
                  </>,
                ));
              case 'COMPLAINT':
                return [row('complaints', g.kind,
                  t('dash.group.complaints', { count: g.count }),
                  t('dash.group.complaintsDetail', {
                    title: g.oldest.title,
                    age: g.oldestDays === 0 ? t('dash.action.today') : t('dash.action.daysAgo', { count: g.oldestDays }),
                  }),
                  <Button size="small" component={RouterLink} to={g.count === 1 ? `/complaints/${g.oldest.id}` : '/complaints'}>{t('dash.group.open')}</Button>,
                )];
              case 'ARREARS':
                return [row('arrears', g.kind,
                  t('dash.group.arrears', { count: g.count }),
                  g.serious > 0
                    ? t('dash.group.arrearsSerious', { serious: g.serious, amount: formatRupiah(g.owedCents) })
                    : t('dash.group.arrearsAmount', { amount: formatRupiah(g.owedCents) }),
                  <Button size="small" component={RouterLink} to="/reminders">{t('dash.group.remind')}</Button>,
                )];
            }
          })}
        </Box>
      )}
    </Card>
  );
}
